import axios from "axios";

// Supplier logo resolution + upload.
//
// A logo can live in two places:
//   1. the frontend public folder as /{SUPPLIERCODE}.{ext} — the original
//      convention, where nearly every existing logo lives;
//   2. the backend store, written by the admin logo upload and served
//      extension-agnostically at GET /admin/supplier-logo/:code.
//
// ORDER MATTERS FOR SPEED. The static public file is served by the same host as
// the app and is usually already cached, so it must be tried FIRST. Putting the
// API first cost a cross-origin round trip on every supplier switch — and while
// it was in flight the <img> kept painting the PREVIOUS supplier's logo, which
// reads as the app showing the wrong logo for seconds at a time.
//
// Keep this list SHORT. Every extra entry is a sequential failed request before
// anything renders. `.jpg` covers ~every committed logo, `.png` covers the rest
// (DEMO), and the API resolver is extension-agnostic — it finds .jpeg/.webp and
// anything newly uploaded — so probing more extensions here is redundant.

const PUBLIC_EXTENSIONS = ["jpg", "png"];

export const BLANK_LOGO = "/blank.jpg";
export const LOGO_MAX_BYTES = 4 * 1024 * 1024;
export const LOGO_ACCEPT = "image/jpeg,image/png,image/webp";

/**
 * @param {string} supplierCode
 * @param {object} [options]
 * @param {string|number} [options.cacheBust] bump after an upload — logos are
 *   served with Cache-Control max-age=300, so without this the browser keeps
 *   showing the previous image.
 * @param {boolean} [options.preferApi] put the API resolver first. Only for the
 *   admin Supplier Logos screen straight after an upload, where seeing the NEW
 *   logo matters more than the one-round-trip cost. Never for the app chrome.
 */
export const buildLogoCandidates = (supplierCode, options = {}) => {
  if (!supplierCode) return [BLANK_LOGO];

  const {cacheBust, preferApi = false} = options;
  const apiBase = process.env.REACT_APP_API_URL;
  const suffix = cacheBust ? `?v=${encodeURIComponent(cacheBust)}` : "";

  const staticUrls = PUBLIC_EXTENSIONS.map((ext) => `/${encodeURIComponent(supplierCode)}.${ext}${suffix}`);
  const apiUrls = apiBase ? [`${apiBase}/admin/supplier-logo/${encodeURIComponent(supplierCode)}${suffix}`] : [];

  return preferApi ? [...apiUrls, ...staticUrls, BLANK_LOGO] : [...staticUrls, ...apiUrls, BLANK_LOGO];
};

// onError handler helper: advance to the next candidate, stopping at /blank.jpg
// so a missing blank.jpg cannot cause an infinite error loop.
export const nextLogoCandidate = (currentTarget, candidates) => {
  const currentIndex = candidates.indexOf(currentTarget.getAttribute("src"));
  const next = candidates[currentIndex + 1];
  if (!next || currentIndex === -1) {
    currentTarget.onerror = null;
    currentTarget.src = BLANK_LOGO;
    return;
  }
  if (next === BLANK_LOGO) currentTarget.onerror = null;
  currentTarget.src = next;
};

/**
 * Client-side pre-check so an obviously-wrong file never leaves the browser.
 * The backend re-validates by magic bytes regardless.
 * @returns {string|null} error message, or null when acceptable
 */
export const validateLogoFile = (file) => {
  if (!file) return "No file selected.";
  if (!LOGO_ACCEPT.split(",").includes(file.type)) return "Unsupported image type. Choose a JPG, PNG or WEBP file.";
  if (file.size > LOGO_MAX_BYTES) return `${(file.size / 1024 / 1024).toFixed(1)}MB — the maximum is ${LOGO_MAX_BYTES / 1024 / 1024}MB.`;
  return null;
};

/** Shared by AddUser (new supplier) and SupplierLogos (existing supplier). */
export const uploadSupplierLogo = async (file, suppliercode) => {
  const formData = new FormData();
  formData.append("logo", file);
  formData.append("suppliercode", suppliercode);
  const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/upload-supplier-logo`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};
